import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import Notification from '../components/Notification';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PageEditor } from '../components/lesson-editors/PageEditors';
import { useNotification } from '../hooks/useNotification';

const ExamEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [examInfo, setExamInfo] = useState({
    title: '',
    description: '',
    passingScore: 80,
    timeLimit: null,
    isActive: true,
  });

  const [examData, setExamData] = useState({
    id: `exam-${Date.now()}`,
    title: '',
    description: '',
    passingScore: 80,
    timeLimit: null,
    pages: [],
    examAnswers: {},
  });

  const [course, setCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pageDeleteModal, setPageDeleteModal] = useState({ isOpen: false, pageIndex: null, pageType: '' });
  const { notification, showError, showSuccess, hideNotification } = useNotification();

  useEffect(() => {
    if (courseId) {
      fetchCourse();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/courses/${courseId}/details`);
      const courseData = response.data.data.course;
      
      setCourse(courseData);

      const existingExamData = courseData.examData;
      if (existingExamData) {
        setExamInfo({
          title: existingExamData.title || '',
          description: existingExamData.description || '',
          passingScore: existingExamData.passingScore || 80,
          timeLimit: existingExamData.timeLimit || null,
          isActive: existingExamData.isActive !== false,
        });

        setExamData(existingExamData);
      } else {
        setExamData({
          id: `exam-${Date.now()}`,
          title: 'Course Exam',
          description: '',
          passingScore: 80,
          timeLimit: null,
          pages: [],
          examAnswers: {},
        });
      }
    } catch (err) {
      setError('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        examData: {
          ...examData,
          title: examInfo.title,
          description: examInfo.description,
          passingScore: examInfo.passingScore,
          timeLimit: examInfo.timeLimit,
          isActive: examInfo.isActive,
        },
      };

      const response = await api.patch(`/admin/courses/${courseId}`, payload);

      if (response.data.success) {
        setSuccess('Exam saved successfully!');
        showSuccess('Exam saved successfully!');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Failed to save exam';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePageClick = (index) => {
    const page = examData.pages[index];
    setPageDeleteModal({
      isOpen: true,
      pageIndex: index,
      pageType: page?.type || 'unknown',
    });
  };

  const handleDeletePageConfirm = () => {
    const { pageIndex } = pageDeleteModal;
    if (pageIndex !== null) {
      const newPages = examData.pages.filter((_, i) => i !== pageIndex);
      setExamData(prev => ({
        ...prev,
        pages: newPages.map((page, index) => ({
          ...page,
          pageNumber: index,
        })),
      }));
    }
    setPageDeleteModal({ isOpen: false, pageIndex: null, pageType: '' });
  };

  const handleDeletePageCancel = () => {
    setPageDeleteModal({ isOpen: false, pageIndex: null, pageType: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Exam Editor</h1>
            {course && (
              <p className="text-gray-600 mt-1">
                Course: {course.title}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back to Course
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Exam'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {['basic', 'pages', 'preview'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'basic' && (
            <BasicInfoTab
              examInfo={examInfo}
              setExamInfo={setExamInfo}
            />
          )}
          {activeTab === 'pages' && (
            <PagesTab
              examData={examData}
              setExamData={setExamData}
              onDeletePageClick={handleDeletePageClick}
              courseId={courseId}
            />
          )}
          {activeTab === 'preview' && (
            <PreviewTab examData={examData} examInfo={examInfo} />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={pageDeleteModal.isOpen}
        title="Delete Page"
        message={`Are you sure you want to delete this ${pageDeleteModal.pageType} page? This action cannot be undone.`}
        onConfirm={handleDeletePageConfirm}
        onCancel={handleDeletePageCancel}
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="bg-red-600 hover:bg-red-700"
      />

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
    </div>
  );
};

const BasicInfoTab = ({ examInfo, setExamInfo }) => {
  const handleChange = (field, value) => {
    setExamInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
        <input
          type="text"
          value={examInfo.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Course Exam"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={examInfo.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Exam description..."
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Passing Score (%)</label>
          <input
            type="number"
            value={examInfo.passingScore || 80}
            onChange={(e) => handleChange('passingScore', parseInt(e.target.value) || 80)}
            min="0"
            max="100"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Time Limit (minutes)</label>
          <input
            type="number"
            value={examInfo.timeLimit || ''}
            onChange={(e) => handleChange('timeLimit', e.target.value ? parseInt(e.target.value) : null)}
            min="1"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="flex items-center">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={examInfo.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <span className="ml-2 text-sm text-gray-700">Active</span>
        </label>
      </div>
    </div>
  );
};

const PagesTab = ({ examData, setExamData, onDeletePageClick, courseId }) => {
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [selectedPageType, setSelectedPageType] = useState('');

  const updatePageNumbers = (pages) => {
    return pages.map((page, index) => ({
      ...page,
      pageNumber: index,
    }));
  };

  const addPage = (type, insertPosition = null) => {
    const newPage = {
      id: `page-${Date.now()}`,
      type,
      pageNumber: insertPosition !== null ? insertPosition : examData.pages.length,
    };

    if (type === 'exam-intro') {
      newPage.title = 'Exam Introduction';
      newPage.blocks = [];
    } else if (type === 'exam-question') {
      newPage.question = {
        id: `q-${Date.now()}`,
        text: '',
        type: 'multiple-choice',
        points: 1,
      };
      newPage.options = [];
      newPage.questionNumber = examData.pages.filter(p => p.type === 'exam-question').length + 1;
    } else if (type === 'exam-results') {
      newPage.title = 'Exam Results';
    }

    setExamData((prev) => {
      let newPages;
      if (insertPosition !== null && insertPosition >= 0 && insertPosition <= prev.pages.length) {
        newPages = [...prev.pages];
        newPages.splice(insertPosition, 0, newPage);
      } else {
        newPages = [...prev.pages, newPage];
      }
      return {
        ...prev,
        pages: updatePageNumbers(newPages),
      };
    });
  };

  const handlePageTypeSelect = (type) => {
    if (type) {
      setSelectedPageType(type);
      setShowPositionModal(true);
    }
  };

  const handlePositionConfirm = (position) => {
    addPage(selectedPageType, position);
    setShowPositionModal(false);
    setSelectedPageType('');
  };

  const movePage = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === examData.pages.length - 1) return;

    setExamData((prev) => {
      const newPages = [...prev.pages];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newPages[index], newPages[targetIndex]] = [newPages[targetIndex], newPages[index]];
      return {
        ...prev,
        pages: updatePageNumbers(newPages),
      };
    });
  };

  const pageTypes = [
    { value: 'exam-intro', label: 'Introduction', icon: '📄' },
    { value: 'exam-question', label: 'Question', icon: '❓' },
    { value: 'exam-results', label: 'Results', icon: '' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Pages ({examData.pages.length})</h3>
        <div className="flex gap-2">
          {pageTypes.map((pageType) => (
            <button
              key={pageType.value}
              onClick={() => handlePageTypeSelect(pageType.value)}
              className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors flex items-center gap-2"
            >
              {pageType.icon && <span>{pageType.icon}</span>}
              <span>Add {pageType.label}</span>
            </button>
          ))}
        </div>
      </div>

      {examData.pages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">No pages yet. Add your first page to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {examData.pages.map((page, index) => (
            <PageEditor
              key={page.id}
              page={page}
              index={index}
              onUpdate={(updates) => {
                const newPages = [...examData.pages];
                newPages[index] = { ...newPages[index], ...updates };
                
                let updatedExamData = { ...examData, pages: newPages };
                if (updates._updateExamAnswers) {
                  updatedExamData = {
                    ...updatedExamData,
                    examAnswers: {
                      ...(examData.examAnswers || {}),
                      ...updates._updateExamAnswers
                    }
                  };
                  delete newPages[index]._updateExamAnswers;
                }
                
                setExamData(updatedExamData);
              }}
              onDelete={() => onDeletePageClick(index)}
              onMoveUp={() => movePage(index, 'up')}
              onMoveDown={() => movePage(index, 'down')}
              canMoveUp={index > 0}
              canMoveDown={index < examData.pages.length - 1}
              checkpoints={[]}
              lessonData={examData}
              moduleId={courseId}
            />
          ))}
        </div>
      )}

      {showPositionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Page</h3>
            <p className="text-gray-600 mb-4">Where would you like to add this page?</p>
            <div className="space-y-2">
              <button
                onClick={() => handlePositionConfirm(0)}
                className="w-full px-4 py-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg"
              >
                At the beginning
              </button>
              <button
                onClick={() => handlePositionConfirm(examData.pages.length)}
                className="w-full px-4 py-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg"
              >
                At the end
              </button>
            </div>
            <button
              onClick={() => setShowPositionModal(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Preview Tab 
const PreviewTab = ({ examData, examInfo }) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Exam Preview</h3>
        <p className="text-sm text-blue-700">
          This is a preview of the exam structure. The actual exam will be displayed to students when they complete all lessons.
        </p>
      </div>

      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2">{examInfo.title || 'Untitled Exam'}</h4>
          <p className="text-sm text-gray-600 mb-2">{examInfo.description || 'No description'}</p>
          <div className="flex gap-4 text-sm text-gray-600">
            <span>Passing Score: {examInfo.passingScore}%</span>
            {examInfo.timeLimit && <span>Time Limit: {examInfo.timeLimit} minutes</span>}
          </div>
        </div>

        <div className="space-y-2">
          <h5 className="font-semibold text-gray-700">Pages ({examData.pages.length})</h5>
          {examData.pages.length === 0 ? (
            <p className="text-gray-500 text-sm">No pages added yet.</p>
          ) : (
            examData.pages.map((page, index) => (
              <div key={page.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Page {index + 1}: {page.type}
                    </span>
                    {page.title && (
                      <span className="ml-2 text-sm text-gray-600">- {page.title}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamEditor;
